import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import AssetCardModal, { userCanUpload, userIsAdmin, PROP_TYPES } from '../components/AssetCardModal';

// ── Filter pills ──────────────────────────────────────────────────────────────

const PILL_VALUES = ['', ...PROP_TYPES];
const PILL_LABELS = {
  '':              'All',
  'costume':       'Costume',
  'branded merch': 'Branded Merch',
  'set dressing':  'Set Dressing',
  'hand prop':     'Hand Prop',
  'food':          'Food',
  'other':         'Other',
};

// ── Upload queue item ─────────────────────────────────────────────────────────

function UploadQueueItem({ item, onDismiss }) {
  const { file, state, progress, error, asset } = item;

  const label = {
    queued:    'Queued',
    uploading: `Uploading ${progress}%`,
    analyzing: 'Analyzing…',
    complete:  'Complete',
    failed:    'Analysis failed',
    error:     error || 'Error',
  }[state] || state;

  const labelColor = {
    complete:  'var(--s-approved-t)',
    failed:    'var(--s-incomplete-t)',
    error:     'var(--s-incomplete-t)',
    analyzing: 'var(--violet)',
    uploading: 'var(--tx-secondary)',
  }[state] || 'var(--tx-muted)';

  return (
    <div style={{
      background: 'var(--bg-card2)', border: '1px solid var(--border)',
      borderRadius: 'var(--radius-sm)', padding: '9px 11px',
      display: 'flex', flexDirection: 'column', gap: 6,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{ width: 34, height: 34, borderRadius: 5, flexShrink: 0, overflow: 'hidden', background: 'var(--bg-input)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {asset?.thumbnailUrl
            ? <img src={asset.thumbnailUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            : <span style={{ fontSize: 15, color: 'var(--tx-faint)' }}>
                {state === 'complete' ? '✓' : state === 'error' || state === 'failed' ? '✕' : '◌'}
              </span>}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 12, color: 'var(--tx-primary)', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {file.name}
          </div>
          <div style={{ fontSize: 11, color: labelColor, marginTop: 1 }}>{label}</div>
        </div>
        <button onClick={onDismiss} style={{ background: 'none', border: 'none', color: 'var(--tx-faint)', cursor: 'pointer', fontSize: 12, padding: '2px 4px', flexShrink: 0, lineHeight: 1 }}>✕</button>
      </div>

      {state === 'uploading' && (
        <div style={{ height: 3, background: 'var(--bg-input)', borderRadius: 2, overflow: 'hidden' }}>
          <div style={{ height: '100%', width: `${progress}%`, background: 'var(--violet)', borderRadius: 2, transition: 'width 0.25s' }} />
        </div>
      )}
      {state === 'analyzing' && (
        <div style={{ height: 3, background: 'var(--bg-input)', borderRadius: 2, overflow: 'hidden', position: 'relative' }}>
          <div style={{ height: '100%', width: '35%', background: 'var(--violet)', borderRadius: 2, animation: 'ale-scan 1.5s ease-in-out infinite' }} />
        </div>
      )}
    </div>
  );
}

// ── Upload panel ──────────────────────────────────────────────────────────────

function UploadPanel({ onUploadComplete, folderId }) {
  const [queue,    setQueue]    = useState([]);
  const [dragging, setDragging] = useState(false);
  const [clients,  setClients]  = useState([]);
  const [clientId, setClientId] = useState('');
  const fileInputRef = useRef(null);
  const pollTimers   = useRef({});

  useEffect(() => {
    api.get('/api/clients').then(r => setClients(r.data)).catch(() => {});
  }, []);

  useEffect(() => () => { Object.values(pollTimers.current).forEach(clearInterval); }, []);

  function updateItem(uid, patch) {
    setQueue(q => q.map(item => item.uid === uid ? { ...item, ...patch } : item));
  }

  function startPolling(uid, assetId) {
    pollTimers.current[uid] = setInterval(async () => {
      try {
        const res = await api.get(`/api/assets/${assetId}`);
        const a = res.data;
        if (a.analysisStatus === 'complete' || a.analysisStatus === 'failed') {
          clearInterval(pollTimers.current[uid]);
          delete pollTimers.current[uid];
          updateItem(uid, { state: a.analysisStatus === 'complete' ? 'complete' : 'failed', asset: a });
          if (a.analysisStatus === 'complete') onUploadComplete?.();
        }
      } catch { /* keep polling */ }
    }, 2000);
  }

  async function processFile(uid, file) {
    updateItem(uid, { state: 'uploading', progress: 0 });
    try {
      const presignRes = await api.post('/api/assets/presign', {
        fileName:    file.name,
        fileSize:    file.size,
        contentType: file.type || 'image/jpeg',
      });
      const { uploadUrl, assetId } = presignRes.data;
      updateItem(uid, { assetId });

      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.upload.addEventListener('progress', e => {
          if (e.lengthComputable) updateItem(uid, { progress: Math.round((e.loaded / e.total) * 100) });
        });
        xhr.addEventListener('load', () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`HTTP ${xhr.status}`))));
        xhr.addEventListener('error', () => reject(new Error('Network error')));
        xhr.open('PUT', uploadUrl);
        xhr.setRequestHeader('Content-Type', file.type || 'image/jpeg');
        xhr.send(file);
      });

      await api.post('/api/assets/confirm', {
        assetId,
        folderId:  folderId  || null,
        clientId:  clientId  || null,
      });
      updateItem(uid, { state: 'analyzing', progress: 100 });
      startPolling(uid, assetId);
    } catch (err) {
      updateItem(uid, { state: 'error', error: err.response?.data?.error || err.message });
    }
  }

  function handleFiles(files) {
    const toAdd = [];
    for (const file of files) {
      const uid  = `${Date.now()}_${Math.random()}`;
      const over = file.size > 20 * 1024 * 1024;
      toAdd.push({ uid, file, state: over ? 'error' : 'queued', progress: 0, error: over ? 'File exceeds 20 MB.' : null, asset: null, assetId: null });
    }
    setQueue(q => [...q, ...toAdd]);
    toAdd.filter(i => i.state !== 'error').forEach(i => processFile(i.uid, i.file));
  }

  function dismissItem(uid) {
    clearInterval(pollTimers.current[uid]);
    delete pollTimers.current[uid];
    setQueue(q => q.filter(i => i.uid !== uid));
  }

  const hasFinished = queue.some(i => i.state === 'complete' || i.state === 'error' || i.state === 'failed');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {clients.length > 0 && (
        <select
          className="vc-input"
          value={clientId}
          onChange={e => setClientId(e.target.value)}
          style={{ fontSize: 12 }}
        >
          <option value="">No project (shared)</option>
          {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      )}

      <div
        onDragOver={e => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={e => {
          e.preventDefault(); setDragging(false);
          const files = Array.from(e.dataTransfer.files).filter(f => f.type.startsWith('image/') || f.name.toLowerCase().endsWith('.heic'));
          if (files.length) handleFiles(files);
        }}
        onClick={() => fileInputRef.current?.click()}
        style={{
          border: `2px dashed ${dragging ? 'var(--violet)' : 'var(--border-mid)'}`,
          borderRadius: 'var(--radius-md)',
          background: dragging ? 'var(--violet-soft)' : 'var(--bg-input)',
          padding: '20px 16px', textAlign: 'center', cursor: 'pointer',
          transition: 'all 0.15s',
        }}
      >
        <div style={{ fontSize: 20, marginBottom: 6, color: dragging ? 'var(--violet)' : 'var(--tx-muted)' }}>⬆</div>
        <div style={{ fontSize: 12, color: 'var(--tx-secondary)', fontWeight: 500 }}>
          Drop images or <span style={{ color: 'var(--violet)' }}>browse</span>
        </div>
        <div style={{ fontSize: 10, color: 'var(--tx-faint)', marginTop: 5, lineHeight: 1.5 }}>
          JPEG · PNG · WebP · GIF · HEIC — max 20 MB per file
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif,image/heic,.heic"
          multiple
          style={{ display: 'none' }}
          onChange={e => { if (e.target.files?.length) { handleFiles(Array.from(e.target.files)); e.target.value = ''; } }}
        />
      </div>

      {queue.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          {hasFinished && (
            <button
              onClick={() => setQueue(q => q.filter(i => i.state !== 'complete' && i.state !== 'error' && i.state !== 'failed'))}
              style={{ alignSelf: 'flex-end', background: 'none', border: 'none', color: 'var(--tx-faint)', fontSize: 11, cursor: 'pointer', padding: '0 2px' }}
            >
              Clear done
            </button>
          )}
          {queue.map(item => (
            <UploadQueueItem key={item.uid} item={item} onDismiss={() => dismissItem(item.uid)} />
          ))}
        </div>
      )}
    </div>
  );
}

// ── ZIP upload panel ──────────────────────────────────────────────────────────

function ZipUploadPanel({ folderId, onComplete }) {
  const [state,    setState]    = useState('idle');
  const [progress, setProgress] = useState(0);
  const [summary,  setSummary]  = useState(null);
  const [errMsg,   setErrMsg]   = useState(null);
  const inputRef = useRef(null);

  async function handleFile(file) {
    setState('uploading'); setProgress(0); setSummary(null); setErrMsg(null);
    const fd = new FormData();
    fd.append('zip', file);
    if (folderId) fd.append('folderId', folderId);
    try {
      const r = await api.post('/api/assets/upload-zip', fd, {
        onUploadProgress: e => { if (e.total) setProgress(Math.round((e.loaded / e.total) * 100)); },
      });
      setSummary(r.data);
      setState('done');
      onComplete?.();
    } catch (err) {
      setErrMsg(err.response?.data?.error || err.message || 'Upload failed.');
      setState('error');
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <button
        onClick={() => { if (state !== 'uploading') inputRef.current?.click(); }}
        disabled={state === 'uploading'}
        style={{
          width: '100%', padding: '8px 12px',
          background: 'var(--bg-card2)', border: '1px solid var(--border)',
          borderRadius: 'var(--radius-sm)',
          cursor: state === 'uploading' ? 'default' : 'pointer',
          fontSize: 12, color: 'var(--tx-secondary)', textAlign: 'center',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
          opacity: state === 'uploading' ? 0.6 : 1,
        }}
      >
        <span style={{ fontSize: 13 }}>⬆</span> Upload ZIP
      </button>
      <input
        ref={inputRef} type="file" accept=".zip" style={{ display: 'none' }}
        onChange={e => { const f = e.target.files?.[0]; if (f) { handleFile(f); e.target.value = ''; } }}
      />

      {state === 'uploading' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          <div style={{ fontSize: 11, color: 'var(--tx-muted)' }}>
            {progress < 100 ? `Uploading ZIP… ${progress}%` : 'Processing images…'}
          </div>
          <div style={{ height: 3, background: 'var(--bg-input)', borderRadius: 2, overflow: 'hidden', position: 'relative' }}>
            {progress < 100
              ? <div style={{ height: '100%', width: `${progress}%`, background: 'var(--violet)', borderRadius: 2, transition: 'width 0.25s' }} />
              : <div style={{ height: '100%', width: '35%', background: 'var(--violet)', borderRadius: 2, animation: 'ale-scan 1.5s ease-in-out infinite' }} />
            }
          </div>
        </div>
      )}

      {state === 'done' && summary && (
        <div style={{ background: 'var(--bg-card2)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 5 }}>
          <div style={{ fontSize: 12, color: 'var(--s-approved-t)', fontWeight: 500 }}>
            {summary.queued} photo{summary.queued !== 1 ? 's' : ''} uploaded — analyzing in the background.
          </div>
          {summary.over_limit && (
            <div style={{ fontSize: 11, color: 'var(--s-filmed-t)' }}>Your ZIP contained more than 50 images — the first 50 were uploaded.</div>
          )}
          {summary.skipped > 0 && (
            <div style={{ fontSize: 11, color: 'var(--tx-faint)' }}>{summary.skipped} file{summary.skipped !== 1 ? 's' : ''} skipped (unsupported or over 20 MB).</div>
          )}
          <button onClick={() => setState('idle')} style={{ alignSelf: 'flex-end', background: 'none', border: 'none', color: 'var(--tx-faint)', fontSize: 11, cursor: 'pointer', padding: '0 2px' }}>
            Dismiss
          </button>
        </div>
      )}

      {state === 'error' && (
        <div style={{ background: 'var(--bg-card2)', border: '1px solid var(--s-incomplete)', borderRadius: 'var(--radius-sm)', padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 5 }}>
          <div style={{ fontSize: 12, color: 'var(--s-incomplete-t)' }}>{errMsg}</div>
          <button onClick={() => setState('idle')} style={{ alignSelf: 'flex-end', background: 'none', border: 'none', color: 'var(--tx-faint)', fontSize: 11, cursor: 'pointer', padding: '0 2px' }}>
            Dismiss
          </button>
        </div>
      )}
    </div>
  );
}

// ── Folder tab button ─────────────────────────────────────────────────────────

function FolderTab({ label, selected, onClick, isAdmin, onRename, onDelete }) {
  const [hovered, setHovered] = useState(false);

  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'flex', alignItems: 'center', gap: 5, flexShrink: 0,
        padding: '5px 14px', borderRadius: 'var(--radius-pill)',
        border: '1px solid',
        borderColor: selected ? 'var(--violet)' : 'var(--border)',
        background: selected ? 'var(--violet-soft)' : hovered ? 'var(--bg-hover)' : 'var(--bg-card2)',
        color: selected ? 'var(--violet)' : 'var(--tx-muted)',
        fontSize: 12, fontWeight: selected ? 600 : 400,
        cursor: 'pointer', transition: 'all 0.12s', whiteSpace: 'nowrap',
      }}
    >
      {label}
      {isAdmin && hovered && (
        <div style={{ display: 'flex', gap: 1 }} onClick={e => e.stopPropagation()}>
          <button onClick={onRename} title="Rename"
            style={{ background: 'none', border: 'none', color: 'var(--tx-faint)', cursor: 'pointer', fontSize: 11, padding: '1px 3px', lineHeight: 1 }}>✎</button>
          <button onClick={onDelete} title="Delete"
            style={{ background: 'none', border: 'none', color: 'var(--tx-faint)', cursor: 'pointer', fontSize: 13, padding: '1px 3px', lineHeight: 1 }}>×</button>
        </div>
      )}
    </div>
  );
}

// ── Folder tabs row ───────────────────────────────────────────────────────────

function FolderTabs({ folders, selectedId, onSelect, isAdmin, onFolderCreated, onFolderRenamed, onFolderDeleted }) {
  const [creating,      setCreating]      = useState(false);
  const [newName,       setNewName]       = useState('');
  const [newClientId,   setNewClientId]   = useState('');
  const [renamingId,    setRenamingId]    = useState(null);
  const [renameVal,     setRenameVal]     = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [clients,       setClients]       = useState([]);
  const [saving,        setSaving]        = useState(false);

  useEffect(() => {
    if (isAdmin) api.get('/api/clients').then(r => setClients(r.data)).catch(() => {});
  }, [isAdmin]);

  async function createFolder() {
    if (!newName.trim()) return;
    setSaving(true);
    try {
      const r = await api.post('/api/asset-folders', { name: newName.trim(), clientId: newClientId || null });
      onFolderCreated(r.data);
      setCreating(false); setNewName(''); setNewClientId('');
    } catch {} finally { setSaving(false); }
  }

  async function renameFolder(id) {
    if (!renameVal.trim()) return;
    setSaving(true);
    try {
      const r = await api.patch(`/api/asset-folders/${id}`, { name: renameVal.trim() });
      onFolderRenamed(r.data);
      setRenamingId(null); setRenameVal('');
    } catch {} finally { setSaving(false); }
  }

  async function deleteFolder(id) {
    try {
      await api.delete(`/api/asset-folders/${id}`);
      onFolderDeleted(id);
      setDeleteConfirm(null);
    } catch {}
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, overflowX: 'auto', paddingBottom: 2, flexWrap: 'wrap' }}>
        {/* All Assets */}
        <button
          onClick={() => onSelect(null)}
          style={{
            flexShrink: 0, padding: '5px 14px', borderRadius: 'var(--radius-pill)',
            border: '1px solid',
            borderColor: !selectedId ? 'var(--violet)' : 'var(--border)',
            background: !selectedId ? 'var(--violet-soft)' : 'var(--bg-card2)',
            color: !selectedId ? 'var(--violet)' : 'var(--tx-muted)',
            fontSize: 12, fontWeight: !selectedId ? 600 : 400,
            cursor: 'pointer', transition: 'all 0.12s', whiteSpace: 'nowrap',
          }}
        >
          All Assets
        </button>

        {/* Folder tabs */}
        {folders.map(folder => {
          if (renamingId === folder.id) {
            return (
              <div key={folder.id} style={{ display: 'flex', gap: 4, flexShrink: 0, alignItems: 'center' }}>
                <input
                  className="vc-input"
                  value={renameVal}
                  onChange={e => setRenameVal(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') renameFolder(folder.id);
                    if (e.key === 'Escape') { setRenamingId(null); setRenameVal(''); }
                  }}
                  autoFocus
                  style={{ width: 130, fontSize: 12, padding: '3px 8px' }}
                />
                <button onClick={() => renameFolder(folder.id)} disabled={saving}
                  style={{ background: 'var(--violet)', border: 'none', color: '#fff', borderRadius: 'var(--radius-sm)', padding: '3px 8px', fontSize: 11, cursor: 'pointer' }}>✓</button>
                <button onClick={() => { setRenamingId(null); setRenameVal(''); }}
                  style={{ background: 'none', border: 'none', color: 'var(--tx-faint)', cursor: 'pointer', fontSize: 13, padding: '3px 5px' }}>✕</button>
              </div>
            );
          }

          if (deleteConfirm === folder.id) {
            return (
              <div key={folder.id} style={{ display: 'flex', gap: 6, alignItems: 'center', background: 'var(--bg-card2)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', padding: '4px 10px', flexShrink: 0 }}>
                <span style={{ fontSize: 11, color: 'var(--tx-muted)', whiteSpace: 'nowrap' }}>Delete "{folder.name}"?</span>
                <button onClick={() => deleteFolder(folder.id)}
                  style={{ background: 'none', border: '1px solid rgba(212,85,85,0.4)', color: 'var(--s-incomplete-t)', borderRadius: 'var(--radius-sm)', padding: '2px 8px', fontSize: 11, cursor: 'pointer', whiteSpace: 'nowrap' }}>Delete</button>
                <button onClick={() => setDeleteConfirm(null)}
                  style={{ background: 'none', border: 'none', color: 'var(--tx-faint)', fontSize: 11, cursor: 'pointer', padding: '2px 4px' }}>Cancel</button>
              </div>
            );
          }

          return (
            <FolderTab
              key={folder.id}
              label={folder.name}
              selected={selectedId === folder.id}
              onClick={() => onSelect(folder.id)}
              isAdmin={isAdmin}
              onRename={() => { setRenamingId(folder.id); setRenameVal(folder.name); }}
              onDelete={() => setDeleteConfirm(folder.id)}
            />
          );
        })}

        {isAdmin && !creating && (
          <button
            onClick={() => setCreating(true)}
            style={{ flexShrink: 0, background: 'none', border: '1px dashed var(--border-mid)', color: 'var(--tx-faint)', borderRadius: 'var(--radius-pill)', padding: '4px 12px', fontSize: 11, cursor: 'pointer', whiteSpace: 'nowrap' }}
          >
            + Folder
          </button>
        )}
      </div>

      {creating && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <input
            className="vc-input"
            placeholder="Folder name…"
            value={newName}
            onChange={e => setNewName(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') createFolder();
              if (e.key === 'Escape') { setCreating(false); setNewName(''); setNewClientId(''); }
            }}
            autoFocus
            style={{ fontSize: 12, width: 180 }}
          />
          {clients.length > 0 && (
            <select className="vc-input" value={newClientId} onChange={e => setNewClientId(e.target.value)} style={{ fontSize: 12, width: 160 }}>
              <option value="">No project</option>
              {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          )}
          <button className="btn-primary" onClick={createFolder} disabled={saving || !newName.trim()} style={{ fontSize: 11, padding: '5px 12px' }}>
            {saving ? 'Creating…' : 'Create'}
          </button>
          <button className="btn-ghost" onClick={() => { setCreating(false); setNewName(''); setNewClientId(''); }} style={{ fontSize: 11, padding: '5px 10px' }}>
            Cancel
          </button>
        </div>
      )}
    </div>
  );
}

// ── Asset grid card ───────────────────────────────────────────────────────────

function AssetGridCard({ asset, onClick, selectable, checked, onToggleSelect }) {
  const tags    = asset.aiTags || {};
  const topTags = (tags.themes || []).slice(0, 3);

  return (
    <div
      onClick={onClick}
      style={{
        background: 'var(--bg-card)', border: checked ? '1px solid var(--violet)' : '1px solid var(--border)',
        borderRadius: 'var(--radius-md)', overflow: 'hidden', cursor: 'pointer',
        transition: 'border-color 0.15s, transform 0.12s',
        boxShadow: 'var(--shadow-card)',
      }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--border-mid)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = checked ? 'var(--violet)' : 'var(--border)'; e.currentTarget.style.transform = 'translateY(0)'; }}
    >
      <div style={{ aspectRatio: '4/3', background: 'var(--bg-input)', position: 'relative', overflow: 'hidden' }}>
        {selectable && (
          <div
            onClick={e => e.stopPropagation()}
            style={{
              position: 'absolute', top: 6, left: 6, zIndex: 2,
              background: 'rgba(0,0,0,0.55)', borderRadius: 5, padding: '3px 5px',
              display: 'flex', alignItems: 'center', backdropFilter: 'blur(4px)',
            }}
          >
            <input
              type="checkbox"
              checked={checked}
              onChange={onToggleSelect}
              style={{ accentColor: 'var(--violet)', cursor: 'pointer' }}
            />
          </div>
        )}
        {asset.thumbnailUrl ? (
          <img src={asset.thumbnailUrl} alt={asset.fileName} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
        ) : (
          <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {asset.analysisStatus === 'pending'
              ? <span style={{ fontSize: 11, color: 'var(--violet)', animation: 'ale-scan 1.5s ease-in-out infinite' }}>Analyzing…</span>
              : <span style={{ fontSize: 28, color: 'var(--tx-faint)' }}>▣</span>}
          </div>
        )}
        {tags.prop_type && (
          <div style={{
            position: 'absolute', top: 6, right: 6,
            background: 'rgba(0,0,0,0.72)', color: '#fff', backdropFilter: 'blur(4px)',
            fontSize: 9, fontWeight: 700, padding: '2px 7px',
            borderRadius: 'var(--radius-pill)', textTransform: 'uppercase', letterSpacing: '0.05em',
          }}>
            {tags.prop_type}
          </div>
        )}
      </div>

      <div style={{ padding: '9px 11px 11px' }}>
        <div style={{ fontSize: 12, color: 'var(--tx-primary)', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginBottom: 3 }}>
          {asset.fileName}
        </div>
        {asset.aiDescription && (
          <div style={{
            fontSize: 11, color: 'var(--tx-muted)', lineHeight: 1.4,
            overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
          }}>
            {asset.aiDescription}
          </div>
        )}
        {topTags.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 7 }}>
            {topTags.map((t, i) => (
              <span key={i} style={{
                fontSize: 9, padding: '2px 7px', borderRadius: 'var(--radius-pill)',
                background: 'rgba(77,170,112,0.12)', color: 'var(--s-approved-t)', fontWeight: 600, whiteSpace: 'nowrap',
              }}>
                {t}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Browse panel ──────────────────────────────────────────────────────────────

function BrowsePanel({ refreshKey, onCardClick, folders, selectedFolder, onSelectFolder, isAdmin, canBulkDelete, onFolderCreated, onFolderRenamed, onFolderDeleted }) {
  const [searchInput, setSearchInput] = useState('');
  const [q,           setQ]           = useState('');
  const [propType,    setPropType]    = useState('');
  const [clientId,    setClientId]    = useState('');
  const [clients,     setClients]     = useState([]);
  const [assets,      setAssets]      = useState([]);
  const [total,       setTotal]       = useState(0);
  const [searchType,  setSearchType]  = useState(null);
  const [loading,     setLoading]     = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const pageRef = useRef(1);

  // Bulk delete (owner order 2026-09-02). Asset delete is PERMANENT — the
  // cloud file + thumbnail are removed, no recycle bin — so the warning popup
  // says exactly that. Per-asset permission (Admin/Power User or editor
  // cross-delete) is enforced server-side; refusals are skipped and reported.
  const [selectedAssets, setSelectedAssets] = useState(new Set());
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkDeleting,   setBulkDeleting]   = useState(false);
  useEffect(() => {
    setSelectedAssets(prev => {
      if (prev.size === 0) return prev;
      const visible = new Set(assets.map(a => a.id));
      const next = new Set([...prev].filter(id => visible.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [assets]);
  function toggleAssetSelect(id) {
    setSelectedAssets(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }
  async function handleBulkDelete() {
    const ids = [...selectedAssets];
    if (!ids.length || bulkDeleting) return;
    setBulkDeleting(true);
    let deleted = 0;
    const skipped = [];
    for (const id of ids) {
      try {
        await api.delete(`/api/assets/${id}`);
        deleted++;
      } catch (err) {
        const a = assets.find(x => x.id === id);
        skipped.push(`${a?.fileName || id} — ${err.response?.data?.error || 'failed'}`);
      }
    }
    setBulkDeleting(false);
    setBulkDeleteOpen(false);
    setSelectedAssets(new Set());
    load();
    if (skipped.length) {
      alert(`${deleted} of ${ids.length} deleted.\n\nSkipped ${skipped.length}:\n${skipped.slice(0, 10).join('\n')}${skipped.length > 10 ? `\n…and ${skipped.length - 10} more` : ''}`);
    }
  }

  useEffect(() => { api.get('/api/clients').then(r => setClients(r.data)).catch(() => {}); }, []);

  function buildParams(page) {
    const params = new URLSearchParams({ page });
    if (q)              params.set('q', q);
    if (propType)       params.set('prop_type', propType);
    if (clientId)       params.set('client_id', clientId);
    if (selectedFolder) params.set('folder_id', selectedFolder);
    return params;
  }

  const load = useCallback(async () => {
    setLoading(true);
    pageRef.current = 1;
    try {
      const res = await api.get(`/api/assets?${buildParams(1)}`);
      setAssets(res.data.assets);
      setTotal(res.data.total);
      setSearchType(res.data.searchType || null);
    } catch {} finally { setLoading(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, propType, clientId, selectedFolder]);

  useEffect(() => { load(); }, [load, refreshKey]);

  useEffect(() => {
    const t = setTimeout(() => setQ(searchInput.trim()), 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  async function loadMore() {
    setLoadingMore(true);
    try {
      const nextPage = pageRef.current + 1;
      const res = await api.get(`/api/assets?${buildParams(nextPage)}`);
      setAssets(a => [...a, ...res.data.assets]);
      setTotal(res.data.total);
      pageRef.current = nextPage;
    } catch {} finally { setLoadingMore(false); }
  }

  const hasMore = assets.length < total;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Folder tabs */}
      <FolderTabs
        folders={folders}
        selectedId={selectedFolder}
        onSelect={onSelectFolder}
        isAdmin={isAdmin}
        onFolderCreated={onFolderCreated}
        onFolderRenamed={onFolderRenamed}
        onFolderDeleted={onFolderDeleted}
      />

      {/* Search + client filter */}
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <input
          className="vc-input"
          placeholder="Search props, brands, themes…"
          value={searchInput}
          onChange={e => setSearchInput(e.target.value)}
          style={{ flex: 1, minWidth: 200 }}
        />
        {clients.length > 0 && (
          <select className="vc-input" value={clientId} onChange={e => setClientId(e.target.value)} style={{ width: 150, flexShrink: 0 }}>
            <option value="">All projects</option>
            {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        )}
      </div>

      {/* Prop type filter pills */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {PILL_VALUES.map(v => (
          <button
            key={v}
            onClick={() => setPropType(v)}
            style={{
              padding: '4px 14px', borderRadius: 'var(--radius-pill)',
              border: '1px solid',
              borderColor: propType === v ? 'var(--violet)' : 'var(--border)',
              background: propType === v ? 'var(--violet-soft)' : 'var(--bg-card2)',
              color: propType === v ? 'var(--violet)' : 'var(--tx-muted)',
              fontSize: 12, fontWeight: propType === v ? 600 : 400,
              cursor: 'pointer', transition: 'all 0.12s', whiteSpace: 'nowrap',
            }}
          >
            {PILL_LABELS[v]}
          </button>
        ))}
      </div>

      {/* Semantic search banner */}
      {searchType === 'semantic' && (
        <div style={{
          fontSize: 12, color: 'var(--tx-muted)',
          background: 'var(--bg-card2)', border: '1px solid var(--border)',
          borderRadius: 'var(--radius-sm)', padding: '8px 14px',
        }}>
          No exact matches — showing visually similar assets
        </div>
      )}

      {/* Count */}
      {!loading && searchType !== 'semantic_unavailable' && total > 0 && (
        <div style={{ fontSize: 12, color: 'var(--tx-muted)' }}>
          {total} asset{total === 1 ? '' : 's'}
          {(q || propType || clientId) && ' matching filters'}
        </div>
      )}

      {/* Grid */}
      {loading ? (
        <div style={{ padding: '48px 0', textAlign: 'center', color: 'var(--tx-faint)', fontSize: 13 }}>Loading…</div>
      ) : searchType === 'semantic_unavailable' ? (
        <div style={{ padding: '48px 0', textAlign: 'center', color: 'var(--tx-faint)', fontSize: 13 }}>No results found.</div>
      ) : assets.length === 0 ? (
        <div style={{ padding: '48px 0', textAlign: 'center', color: 'var(--tx-faint)', fontSize: 13 }}>
          {q || propType || clientId ? 'No assets found. Try a broader search term.' : 'No assets in the library yet.'}
        </div>
      ) : (
        <>
          {canBulkDelete && selectedAssets.size > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0 12px' }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--tx-primary)' }}>
                {selectedAssets.size} asset{selectedAssets.size !== 1 ? 's' : ''} selected
              </span>
              <button
                onClick={() => setBulkDeleteOpen(true)}
                disabled={bulkDeleting}
                style={{ padding: '5px 14px', fontSize: 12, fontWeight: 600, fontFamily: 'var(--font)', cursor: 'pointer', borderRadius: 'var(--radius-sm)', background: 'rgba(212,85,85,0.15)', color: 'var(--s-incomplete-t)', border: '1px solid rgba(212,85,85,0.4)' }}
              >
                Delete {selectedAssets.size} asset{selectedAssets.size !== 1 ? 's' : ''}…
              </button>
              <button
                className="btn-ghost"
                style={{ fontSize: 12 }}
                onClick={() => setSelectedAssets(new Set(assets.map(a => a.id)))}
                disabled={bulkDeleting}
                title="Select every loaded asset in this view"
              >
                Select all loaded
              </button>
              <button className="btn-ghost" style={{ fontSize: 12 }} onClick={() => setSelectedAssets(new Set())} disabled={bulkDeleting}>
                Clear
              </button>
            </div>
          )}
          <div className="ale-grid">
            {assets.map(asset => (
              <AssetGridCard
                key={asset.id}
                asset={asset}
                onClick={() => onCardClick(asset.id)}
                selectable={canBulkDelete}
                checked={selectedAssets.has(asset.id)}
                onToggleSelect={() => toggleAssetSelect(asset.id)}
              />
            ))}
          </div>
        </>
      )}

      {hasMore && !loading && (
        <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 4 }}>
          <button className="btn-ghost" onClick={loadMore} disabled={loadingMore}>
            {loadingMore ? 'Loading…' : 'Load more'}
          </button>
        </div>
      )}

      {/* Bulk-delete warning popup — asset delete is PERMANENT: the file and
          its thumbnail are removed from cloud storage, no recycle bin. */}
      {bulkDeleteOpen && (
        <div className="modal-overlay" onClick={() => !bulkDeleting && setBulkDeleteOpen(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ width: 480 }}>
            <div className="modal-header">
              <div className="modal-title" style={{ color: 'var(--s-incomplete-t)' }}>
                ⚠ Permanently delete {selectedAssets.size} asset{selectedAssets.size !== 1 ? 's' : ''}?
              </div>
              <button className="vc-close" onClick={() => !bulkDeleting && setBulkDeleteOpen(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div style={{ fontSize: 13, color: 'var(--tx-primary)', lineHeight: 1.6, whiteSpace: 'pre-line' }}>
                {`This deletes the files and their thumbnails from cloud storage and removes them from the Asset Library.\n\nThere is NO recycle bin here — this cannot be undone.\n\nAssets you don't have permission to delete will be skipped and listed.`}
              </div>
            </div>
            <div className="modal-footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button className="btn-ghost" onClick={() => setBulkDeleteOpen(false)} disabled={bulkDeleting}>Cancel</button>
              <button
                onClick={handleBulkDelete}
                disabled={bulkDeleting}
                style={{ padding: '7px 16px', fontSize: 13, fontWeight: 600, fontFamily: 'var(--font)', cursor: 'pointer', borderRadius: 'var(--radius-sm)', background: 'rgba(212,85,85,0.15)', color: 'var(--s-incomplete-t)', border: '1px solid rgba(212,85,85,0.4)' }}
              >
                {bulkDeleting ? 'Deleting…' : `Permanently delete ${selectedAssets.size}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function AssetLibrary() {
  const { user }          = useAuth();
  const canUp             = userCanUpload(user);
  const isAdm             = userIsAdmin(user);
  const [openId,          setOpenId]          = useState(null);
  const [refreshKey,      setRefreshKey]      = useState(0);
  const [folders,         setFolders]         = useState([]);
  const [selectedFolder,  setSelectedFolder]  = useState(null);

  useEffect(() => {
    api.get('/api/asset-folders').then(r => setFolders(r.data)).catch(() => {});
  }, []);

  function handleFolderCreated(f) {
    setFolders(prev => [...prev, f].sort((a, b) => a.name.localeCompare(b.name)));
  }
  function handleFolderRenamed(f) {
    setFolders(prev => prev.map(x => x.id === f.id ? { ...x, name: f.name } : x)
      .sort((a, b) => a.name.localeCompare(b.name)));
  }
  function handleFolderDeleted(id) {
    setFolders(prev => prev.filter(x => x.id !== id));
    if (selectedFolder === id) setSelectedFolder(null);
  }

  return (
    <div className="main">
      <style>{`
        @keyframes ale-scan {
          0%   { transform: translateX(-200%); opacity: 0.7; }
          50%  { opacity: 1; }
          100% { transform: translateX(400%); opacity: 0.7; }
        }
        .ale-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 14px;
        }
        @media (max-width: 1024px) {
          .ale-grid { grid-template-columns: repeat(3, 1fr); }
        }
        @media (max-width: 720px) {
          .ale-grid { grid-template-columns: repeat(2, 1fr); }
        }
        @media (max-width: 480px) {
          .ale-grid { grid-template-columns: 1fr; }
        }
      `}</style>

      <div className="topbar">
        <div>
          <div className="page-title">Asset Library</div>
          <div className="page-sub">
            {canUp ? 'Upload prop photos and browse the library' : 'Browse available props and assets'}
          </div>
        </div>
      </div>

      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '22px 36px 32px', display: 'flex', flexDirection: 'column', gap: 20 }}>

        {/* Upload card — role-gated */}
        {canUp && (
          <div style={{
            background: 'var(--bg-card)', border: '1px solid var(--border)',
            borderRadius: 'var(--radius-md)', padding: '18px 24px',
            display: 'flex', flexDirection: 'column', gap: 14,
          }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--tx-muted)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
              Upload
            </div>
            <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', alignItems: 'flex-start' }}>
              <div style={{ flex: 1, minWidth: 260 }}>
                <UploadPanel onUploadComplete={() => setRefreshKey(k => k + 1)} folderId={selectedFolder} />
              </div>
              <div style={{ width: 200, flexShrink: 0 }}>
                <div style={{ fontSize: 11, color: 'var(--tx-faint)', marginBottom: 8 }}>Bulk upload (ZIP)</div>
                <ZipUploadPanel folderId={selectedFolder} onComplete={() => setRefreshKey(k => k + 1)} />
              </div>
            </div>
          </div>
        )}

        {/* Browse card */}
        <div style={{
          background: 'var(--bg-card)', border: '1px solid var(--border)',
          borderRadius: 'var(--radius-md)', padding: '18px 24px',
          flex: 1,
        }}>
          <BrowsePanel
            refreshKey={refreshKey}
            onCardClick={id => setOpenId(id)}
            folders={folders}
            selectedFolder={selectedFolder}
            onSelectFolder={setSelectedFolder}
            canBulkDelete={!user?.isExternal}
            isAdmin={isAdm}
            onFolderCreated={handleFolderCreated}
            onFolderRenamed={handleFolderRenamed}
            onFolderDeleted={handleFolderDeleted}
          />
        </div>
      </div>

      {openId && (
        <AssetCardModal
          assetId={openId}
          user={user}
          folders={folders}
          onClose={() => setOpenId(null)}
          onArchived={() => { setOpenId(null); setRefreshKey(k => k + 1); }}
          onDeleted={() => { setOpenId(null); setRefreshKey(k => k + 1); }}
          onFolderChanged={() => setRefreshKey(k => k + 1)}
        />
      )}
    </div>
  );
}
